/**
 * Pure mappers for the Racks pages: warehouse rack status → StatusPill tone, and
 * mounted `Item` → `RackItem` for the @ui `RackLayoutView`. Kept side-effect free
 * so both the list and detail pages can share them.
 */
import type { Item, RackStatus } from '@/api/capacities';
import type { RackItem, RackItemStatus } from '@vnetwork/ui/data';

/** Rack lifecycle status → StatusPill tone + label. */
export function rackStatusView(status?: RackStatus): {
  tone: 'success' | 'neutral' | 'warning' | 'info';
  label: string;
} {
  switch (status) {
    case 'active':
      return { tone: 'success', label: 'Active' };
    case 'deprecated':
      return { tone: 'neutral', label: 'Deprecated' };
    case 'reserved':
      return { tone: 'info', label: 'Reserved' };
    case 'maintenance':
      return { tone: 'warning', label: 'Maintenance' };
    default:
      return { tone: 'neutral', label: status ?? '—' };
  }
}

/** Warehouse item.status → RackLayoutView slot status. */
function toRackItemStatus(status?: string): RackItemStatus {
  switch (status) {
    case 'in_use':
    case 'in_datacenter':
    case 'in_service':
      return 'in_use';
    case 'available':
      return 'available';
    case 'loaned':
      return 'reserved';
    case 'damaged':
      return 'faulty';
    case 'maintenance':
      return 'maintenance';
    default:
      return 'other';
  }
}

/** Leading integer of a form factor string ("2U" → 2), else 1. */
function parseHeightU(formFactor?: string): number {
  if (!formFactor) return 1;
  const match = /^\s*(\d+)/.exec(formFactor);
  const n = match ? parseInt(match[1], 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/**
 * Map a mounted warehouse `Item` to a `RackItem`. Items with a 0/missing
 * `rack_position_u` keep positionU 0 → the widget lists them as "unpositioned"
 * (expected with current staging data, where most rows lack a real position).
 */
export function toRackItem(item: Item): RackItem {
  const sublabel = item.service_tag ?? item.ref ?? item.model?.name ?? item.category;
  return {
    id: item.id,
    name: item.name || item.service_tag || item.ref || item.id,
    sublabel,
    positionU: item.rack_position_u ?? 0,
    heightU: parseHeightU(item.form_factor),
    status: toRackItemStatus(item.status),
  };
}
